import { NgZone, OnDestroy, inject, Service } from '@angular/core';
import { ActivatedRoute, Event, NavigationStart, Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { TranslateService } from '@ngx-translate/core';
import Auth0Lock from 'auth0-lock';
import moment from 'moment';
import {
  EMPTY,
  Observable,
  ReplaySubject,
  Subject,
  throwError as observableThrowError,
} from 'rxjs';
import { filter, first, map, mergeMap, takeUntil, tap } from 'rxjs/operators';
import { ServerEnvironmentConfig } from '@core/interfaces/server-environment.interface';
import { IamBackendService } from '@core/providers/backend/iam-backend.service';
import { PermissionsBackendService } from '@core/providers/backend/permissions-backend.service';
import { RolesService } from '@core/providers/backend/roles.service';
import { ContextService } from '@core/providers/context/context.service';
import { NavigationService } from '@core/providers/navigation/navigation.service';
import { NotificationService } from '@core/providers/notification/notification.service';
import { ServerEnvironmentService } from '@core/providers/server-environment/server-environment.service';
import { SessionExpiryService } from '@core/providers/session/session-expiry.service';
import { LocalStorageService } from '@core/providers/storage/local-storage.service';
import { preferredUiLanguage } from '@core/providers/translator/apply-ui-language';
import { UserService } from '@core/providers/user/user.service';
import { ContextUtil } from '@core/utils';
import { CONSTANTS } from '@shared/constants';
import { ContextObject, LoginObject, LoginPayload } from '@shared/interfaces/login-object';
import { isResumableUrl } from '@shared/utils/is-resumable-url';
import { authConfig } from './auth.config';

@Service()
export class AuthService implements OnDestroy {
  private userService = inject(UserService);
  private notificationService = inject(NotificationService);
  private contextService = inject(ContextService);
  private localStorage = inject(LocalStorageService);
  private contextUtil = inject(ContextUtil);
  private permissionsBackendService = inject(PermissionsBackendService);
  private environmentService = inject(ServerEnvironmentService);
  private navigationService = inject(NavigationService);
  private activatedRoute = inject(ActivatedRoute);
  private rolesService = inject(RolesService);
  private iamBackendService = inject(IamBackendService);
  private router = inject(Router);
  private ngZone = inject(NgZone);
  private translateService = inject(TranslateService);
  private sessionExpiryService = inject(SessionExpiryService);

  private jwtHelper: JwtHelperService = new JwtHelperService();
  private serverEnvironmentConfig$: ReplaySubject<Readonly<ServerEnvironmentConfig>> =
    new ReplaySubject<Readonly<ServerEnvironmentConfig>>();
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor() {
    this.environmentService
      .getServerEnvironmentConfig$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((serverEnvironment: ServerEnvironmentConfig) => {
        this.serverEnvironmentConfig$.next(serverEnvironment);
      });
  }

  logIn$(): Observable<Auth0LockStatic> {
    const lockShowOptions: Auth0LockShowOptions = {
      allowLogin: true,
      allowSignUp: false,
    };
    const lockConstructorOptions: Auth0LockConstructorOptions = {
      // Prevent auth0-lock/auth0-js from defaulting to a Cordova plugin constructor
      // which can break in modern bundlers.
      plugins: [],
    };

    return this.serverEnvironmentConfig$.pipe(
      first(),
      map(
        (data: Readonly<ServerEnvironmentConfig>) =>
          new Auth0Lock(data.auth0.clientId, data.auth0.domain, {
            ...authConfig.options,
            ...lockConstructorOptions,
            languageDictionary: {
              title: '',
              signupTitle: this.translateService.instant('LOGIN.SIGN_UP_TITLE'),
              signUpSubmitLabel: this.translateService.instant('LOGIN.SIGN_UP_SUB_TITLE'),
              passwordInputPlaceholder: this.translateService.instant('LOGIN.SET_PASSWORD'),
            },
            language: this.getLangForAuthComponent(),
          })
      ),
      tap((authLock: Auth0LockStatic) => {
        // has to unmount before mounting again
        this.router.events
          .pipe(filter((event: Event) => event instanceof NavigationStart))
          .pipe(first(), takeUntil(this.onDestroy$))
          .subscribe(() => authLock.hide());
        authLock.show(lockShowOptions);
      })
    );
  }

  authenticated(): boolean {
    return this.tokenState() === 'valid';
  }

  hasExpiredSession(): boolean {
    return this.tokenState() === 'expired';
  }

  /**
   * Tears down an expired session and warns the user. Callers navigate afterwards —
   * this deliberately does not, so a navigation cycle that runs several guards
   * cannot fire several competing redirects.
   */
  clearExpiredSession(): void {
    if (!this.translateService.getDefaultLang()) {
      this.translateService.use(preferredUiLanguage());
    }

    this.notificationService.warnWithKey('SHARED.NOTIFICATIONS.SESSION_EXPIRED', undefined, {
      duration: 10000,
    });
    this.removeCookieByName('auth0.ssodata');
    this.localStorage.clear();
    this.userService.setAuthorizedData();
    this.rolesService.clearRoles();
  }

  handleAuthenticated(authResult: AuthResult): void {
    this.contextService.clearContext();
    this.localStorage.clear();
    this.getInternalToken$(authResult)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (response: LoginObject) => {
          this.initTokenData(response);
          this.saveUserLanguage(response);
          this.checkPermissions(response);
        },
        (error: any) => this.handleError$(error)
      );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  setUserLanguage(): void {
    const language = preferredUiLanguage();

    moment.locale(language.split('-')[0]);
    this.translateService.setDefaultLang(language);
    this.translateService.use(language);
  }

  private tokenState(): 'none' | 'valid' | 'expired' {
    const token = localStorage.getItem(CONSTANTS.AUTH.ID_TOKEN);

    if (!token) {
      return 'none';
    }

    try {
      return this.jwtHelper.isTokenExpired(token) ? 'expired' : 'valid';
    } catch {
      // jwtHelper throws on anything that isn't a three-part JWT. A corrupted
      // value is unusable, and letting the error escape would break navigation.
      return 'none';
    }
  }

  private handleRedirect(loginResponse: LoginObject): void {
    const redirectUrl: string = this.activatedRoute.snapshot.queryParams.redirectUrl;
    const type = loginResponse.defaultItem?.type;
    const uuid = loginResponse.defaultItem?.uuid;

    if (this.isProfileIncomplete(loginResponse)) {
      this.navigationService.goToEditProfile();
    } else if (isResumableUrl(redirectUrl)) {
      this.navigationService.goToUrl(redirectUrl);
    } else if (this.rolesService.isAdmin()) {
      this.navigationService.goToAdminAccountManagement();
    } else if (type && uuid && this.contextUtil.isVenueContext(type)) {
      this.navigationService.goToVenueMainScreen(uuid);
    } else if (type && uuid && this.contextUtil.isAccountContext(type)) {
      this.navigationService.goToScoreboards(uuid);
    } else {
      // fallback that should never happen
      this.navigationService.goToEditProfile();
    }
  }

  /**
   * An absent field counts as complete. While the backend has not shipped it, the alternative
   * reading would send every user to the profile page and hold them there.
   */
  private isProfileIncomplete(loginResponse: LoginObject): boolean {
    return loginResponse.profileCompleted === false;
  }

  private initTokenData(result: LoginObject): void {
    localStorage.setItem(CONSTANTS.AUTH.ID_TOKEN, result.foodbackToken);
    this.localStorage.store(CONSTANTS.AUTH.ID_TOKEN, result.foodbackToken);
    this.userService.setAuthorizedData();
    this.sessionExpiryService.reset();
  }

  private initContextData(loginResponse: LoginObject): void {
    this.contextService.clearContext();
    this.userService.setUserData(loginResponse.user);

    if (loginResponse.defaultItem) {
      this.contextService.setFallbackContext(this.toContextObject(loginResponse.defaultItem));
    }

    if (loginResponse.roles.includes(CONSTANTS.ROLES.ADMIN)) {
      this.createContextFromLoginObject$(loginResponse);
    } else {
      // do nothing, as we expect the guards to create the proper context
    }

    this.userService.setProfileIncomplete(this.isProfileIncomplete(loginResponse));
    this.userService.setLoggedIn(true);
    this.userService.setLoginData({
      intercom: loginResponse.intercom,
    });
    this.handleLoginLanding(loginResponse);
  }

  private toContextObject(defaultItem: NonNullable<LoginObject['defaultItem']>): ContextObject {
    return { type: defaultItem.type ?? undefined, uuid: defaultItem.uuid ?? undefined };
  }

  private getInternalToken$(authResult: AuthResult): Observable<LoginObject> {
    const jwtObj: LoginPayload = {
      jwt: authResult.idToken,
    };

    return this.iamBackendService.loginToIam(jwtObj);
  }

  private handleError$(error: Response): Observable<Response> {
    if (!error.ok) {
      const errorMessage: string = (error as any).message || 'Login error';

      this.notificationService.errorSnackbar(errorMessage);
    }

    return observableThrowError(error);
  }

  private handleLoginLanding(loginResponse: LoginObject): void {
    // this is an attempt at a hacky fix for a weird bug that sometimes happens and prevents the content from being visible
    // when it occurs, it looks like Angular doesn't kick the change detection after the NavigationEnd event (I have no idea WHY)
    // tried inspecting it for a few days, didn't succeed, so here's a pseudo fix. Let's hope it works
    this.ngZone.run(() => {
      this.handleRedirect(loginResponse);
    });
  }

  private createContextFromLoginObject$(loginObject: LoginObject): Observable<void> {
    this.rolesService.setRoles(loginObject.roles);

    if (!loginObject.defaultItem) {
      return EMPTY;
    }

    return this.contextService
      .convertDefaultItemToContext$(this.toContextObject(loginObject.defaultItem))
      .pipe(
        mergeMap((context: ContextObject) => {
          this.contextService.setContext(context);

          return new Observable<void>();
        })
      );
  }

  private checkPermissions(loginObject: LoginObject): void {
    this.permissionsBackendService
      .hasAccess$()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((hasLoggedUserAccess: boolean) => {
        if (hasLoggedUserAccess) {
          this.initContextData(loginObject);
        } else {
          // another hacky fix for similar that is related to the comment underneath - for some reason Angular doesn't trigger change
          // detection, leaving the app half-rendered
          this.ngZone.run(() => {
            this.navigationService.goToNoAccess();
          });
        }
      });
  }

  private saveUserLanguage(result: LoginObject): void {
    moment.locale(result.user.language ? result.user.language.split('-')[0] : 'en');
    localStorage.setItem(CONSTANTS.AUTH.LANGUAGE, result.user.language);
    this.setUserLanguage();
  }

  private getLangForAuthComponent(): string {
    return preferredUiLanguage().split('-')[0];
  }

  private removeCookieByName(name: string): void {
    document.cookie = `${name} =; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
  }
}
